'use client'

import type { LocaleString } from '@/.generated/sanityTypes/graphql'
import React, { useEffect, useState } from 'react'
import { useLocale } from 'next-intl'
import type { Locale } from '@/i18n'

export type SubMenuItemProps = {
  _key: string
  menuSlug: string
  menuTitle: LocaleString
}

export const SUBMENU_SCROLL_PADDING = 55

export default function SubMenu({ items }: { items: SubMenuItemProps[] }) {
  const locale = useLocale() as Locale
  const [activeSection, setActiveSection] = useState<string>(items[0].menuSlug)

  useEffect(() => {
    const sections = document.querySelectorAll<HTMLElement>('main > div[id]')

    const handleScroll = () => {
      sections.forEach((section) => {
        const sectionTop = section.offsetTop

        // Set the first visible section to be the active, exit after that
        if (window.scrollY > sectionTop - SUBMENU_SCROLL_PADDING) {
          setActiveSection(section.id)

          return
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const scrollTo = (anchor: string) => {
    const el = document.getElementById(anchor)

    if (!el) return

    window.scrollTo({
      top: el.offsetTop - SUBMENU_SCROLL_PADDING,
      behavior: 'smooth',
    })
  }

  const handleLinkClick = (e: React.MouseEvent, anchor: string) => {
    e.preventDefault()
    scrollTo(anchor)
  }

  return (
    <div className="bg-red sticky top-[65px] z-40">
      {/* Mobile */}
      <select
        className="bg-red mx-auto flex w-fit p-4 text-center text-white outline-none md:hidden"
        onChange={(e) => scrollTo(e.target.value)}
        value={activeSection}
      >
        {items.map((item) => (
          <option key={item._key} value={item.menuSlug}>
            {item.menuTitle[locale]}
          </option>
        ))}
      </select>

      {/* Desktop */}
      <ul className="mx-auto hidden w-full max-w-screen-lg flex-row justify-between gap-6 text-white md:flex 2xl:max-w-screen-xl">
        {items.map((item) => (
          <li key={item._key}>
            <a
              className="block p-4"
              href={`#${item.menuSlug}`}
              onClick={(e) => handleLinkClick(e, item.menuSlug)}
            >
              {item.menuTitle?.[locale]}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
